import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import type { OverdueItem, CategoryDistribution, MostIssuedBook } from "@/types";
import { formatGridDate } from "@/lib/utils";

const COLORS = ["#0f5c52", "#f5a524", "#2dd4a8", "#1a9b8c", "#f07178", "#9aa3b2", "#22b3a2", "#6b7385"];

export default function ReportsPage() {
  const { api } = useAuth();
  const [overdue, setOverdue] = useState<OverdueItem[]>([]);
  const [catDist, setCatDist] = useState<CategoryDistribution[]>([]);
  const [mostIssued, setMostIssued] = useState<MostIssuedBook[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [o, c, m] = await Promise.all([
          api.get("/reports/overdue-list"),
          api.get("/reports/category-distribution"),
          api.get("/reports/most-issued"),
        ]);
        setOverdue(o.data);
        setCatDist(c.data);
        setMostIssued(m.data);
      } catch {
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [api]);

  if (loading) return <div className="flex items-center justify-center py-20 text-muted-foreground">Loading reports...</div>;

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">
          Reports
        </h1>
      </div>

      <Tabs defaultValue="overdue" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overdue" data-testid="tab-overdue">
            Overdue
          </TabsTrigger>
          <TabsTrigger value="categories" data-testid="tab-categories">
            Categories
          </TabsTrigger>
          <TabsTrigger value="popular" data-testid="tab-popular">
            Popular Books
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overdue">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle>Overdue Books ({overdue.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {overdue.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No overdue books</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 font-semibold">Student</th>
                        <th className="text-left py-2 font-semibold">Book</th>
                        <th className="text-left py-2 font-semibold">Accession</th>
                        <th className="text-left py-2 font-semibold">Due Date</th>
                        <th className="text-left py-2 font-semibold">Days Overdue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {overdue.map((t) => (
                        <tr key={t.id} className="border-b hover:bg-muted/30">
                          <td className="py-2">{t.student_name}</td>
                          <td className="py-2">{t.book_title}</td>
                          <td className="py-2 font-mono text-xs">{t.accession_no}</td>
                          <td className="py-2">{formatGridDate(t.due_date)}</td>
                          <td className="py-2">
                            <Badge className="bg-red-100 text-red-800 hover:bg-red-100">{t.days_overdue} days</Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="categories">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle>Books by Category</CardTitle>
            </CardHeader>
            <CardContent>
              {catDist.length > 0 ? (
                <ResponsiveContainer width="100%" height={350}>
                  <PieChart>
                    <Pie
                      data={catDist}
                      dataKey="count"
                      nameKey="category"
                      cx="50%"
                      cy="50%"
                      outerRadius={120}
                      label={({ category, count }: any) => `${category} (${count})`}
                    >
                      {catDist.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-muted-foreground text-center py-8">No data</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="popular">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle>Most Popular Books</CardTitle>
            </CardHeader>
            <CardContent>
              {mostIssued.length > 0 ? (
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={mostIssued.slice(0, 10)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis dataKey="title" tick={{ fontSize: 10 }} angle={-20} textAnchor="end" height={80} />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="#f5a524" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-muted-foreground text-center py-8">No data</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
